import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";
import { getGSTUpdatedList } from "../../../services/GSTService";

const GSTList = () => {
  const dispatch = useDispatch();
  const [gstList, setGstList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  // Fetch GST data
  useEffect(() => {
    fetchGSTData();
  }, []);

  const fetchGSTData = async () => {
    try {
      setLoading(true);

      const response = await getGSTUpdatedList();
      setGstList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching GST list:", error);
      swal("Error", "Failed to fetch GST list", "error");
    } finally {
      setLoading(false);
    }
  };

  // Format data
  useEffect(() => {
    const formatted = (gstList || []).map((item) => ({
      ...item,
      id: item.gst_id,
      date: item.created_at ? new Date(item.created_at).toLocaleDateString() : "-",
      type: item.update_type,
    }));
    setDataList(formatted);
  }, [gstList]);

  // Search + Sort
  const filteredData = dataList
    ?.filter((obj) =>
      (obj.type?.toLowerCase() || "").includes(searchText.toLowerCase()) ||
      (obj.date?.toLowerCase() || "").includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      const A = a.date?.toLowerCase() || "";
      const B = b.date?.toLowerCase() || "";
      return sortOrder === "asc"
        ? A.localeCompare(B)
        : B.localeCompare(A);
    });

  // Reset page on search
  useEffect(() => {
    setCurrentPage(0);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const toggleSort = () => {
    setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) setCurrentPage(page);
  };

  const handleDelete = (item) => {
    swal({
      title: "Delete?",
      text: `Delete GST entry for ${item.date}?`,
      icon: "warning",
      buttons: true,
    }).then(async (yes) => {
      if (!yes) return;

      try {
        await axiosInstance.delete(`gst/${item.id}`);
        swal("Deleted!", "", "success");
        fetchGSTData();
      } catch {
        swal("Error", "Delete failed", "error");
      }
    });
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link to="/add-gst" className="btn btn-primary btn-rounded add-gst">
            + ADD GST PRICE
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
              <table className="table table-striped mb-4">
                <thead className="table-light">
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={toggleSort}>
                      Date {sortOrder === "asc" ? "↑" : "↓"}
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Search date"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>Total GST</th>
                    <th>Type</th>
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedData.map((item) => (
                    <tr key={item.id}>
                      <td>{item.date || "-"}</td>
                      <td>{item.cgst || "-"}</td>
                      <td>{item.sgst || "-"}</td>
                      <td>{item.total_gst || "-"}</td>
                      <td>
                        <span className="badge bg-info">
                          {item.type || "-"}
                        </span>
                      </td>
                      <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                        <span
                          className="preview"
                          onClick={() => swal("View", `Date: ${item.date}\nCGST: ${item.cgst}%\nSGST: ${item.sgst}%\nTotal GST: ${item.total_gst}%\nType: ${item.type}`, "info")}
                          style={{ cursor: "pointer", display: "inline-block" }}
                          title="View GST details"
                        >
                          <i className="fa fa-eye text-info fs-5"></i>
                        </span>
                      </td>
                    </tr>
                  ))}
                  {paginatedData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No GST entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              className="pagination-wrapper"
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <div>
                Showing {filteredData.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                {Math.min(
                  (currentPage + 1) * rowsPerPage,
                  filteredData.length
                )}{" "}
                of {filteredData.length} entries
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                  disabled={currentPage === 0}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                
                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                  const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                  if (startPage > 0) {
                    pages.push(
                      <button
                        key={0}
                        onClick={() => setCurrentPage(0)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        1
                      </button>
                    );
                    if (startPage > 1) {
                      pages.push(
                        <span
                          key="dots1"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i < endPage; i++) {
                    const isActive = currentPage === i;
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: isActive ? "#007bff" : "#fff",
                          color: isActive ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span
                          key="dots2"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages - 1}
                        onClick={() => setCurrentPage(totalPages - 1)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                  disabled={currentPage === totalPages - 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </div>
            </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GSTList;
